/**
 * Live → draft, field by field (admin doc 11 §3, the design's "Review diff").
 *
 * The rows are what a devotee could be charged: every line item, every per-city override, every
 * language/sampradaya amount and the base total. Unchanged rows stay in the review list — "the pandit fee did not move" is part
 * of what a reviewer is signing off — and the publish confirm keeps only the changed ones.
 *
 * Values are text the reviewer reads, never sums: an amount the server resolves later (a fee
 * rule, the travel zone) is named as such rather than shown as ₹0.
 */
import { formatInr } from '../lib/money';
import type { LineItemRow, PriceVersionDetail } from '../lib/api/hooks/pricing';

export type PriceDiffRow = readonly [label: string, before: string, after: string];

/** Where a line's amount comes from when the sheet does not fix it. */
export function amountSource(item: LineItemRow): string {
  if (item.amount_paise !== null && item.amount_paise !== undefined) return 'fixed';
  if (item.fee_rule_id) return `fee rule #${item.fee_rule_id}`;
  if (item.is_travel_resolved) return 'travel zone';
  return 'at quote time';
}

const ABSENT = '—';
/** Always the decimals, as every money screen shows them — ₹2,350 and ₹2,350.50 must line up. */
const PAISE = { paise: true } as const;

function lineValue(item: LineItemRow | undefined): string {
  if (!item) return ABSENT;
  if (item.amount_paise !== null && item.amount_paise !== undefined) return formatInr(item.amount_paise, PAISE);
  return amountSource(item);
}

export function priceVersionDiff(
  live: PriceVersionDetail,
  draft: PriceVersionDetail,
  cityName: (id: number) => string,
  scopeName: (o: { ritual_language_id?: number | null; sampradaya_id?: number | null }) => string,
): PriceDiffRow[] {
  const liveItems = new Map(live.line_items.map((i) => [i.item_type, i]));
  const draftItems = new Map(draft.line_items.map((i) => [i.item_type, i]));
  // The draft's order first, then anything the draft dropped — a removed line is a change too.
  const itemTypes = [
    ...draft.line_items.map((i) => i.item_type),
    ...live.line_items.map((i) => i.item_type).filter((t) => !draftItems.has(t)),
  ];
  // Every type came from one side or the other, so one of the two lookups always answers.
  const labelOf = (type: string) => (draftItems.get(type) ?? liveItems.get(type))!.label;

  const rows: PriceDiffRow[] = itemTypes.map((type) => [
    labelOf(type),
    lineValue(liveItems.get(type)),
    lineValue(draftItems.get(type)),
  ]);

  const key = (o: { city_id: number; item_type: string }) => `${o.city_id}:${o.item_type}`;
  const liveOverrides = new Map(live.city_overrides.map((o) => [key(o), o]));
  const draftOverrides = new Map(draft.city_overrides.map((o) => [key(o), o]));
  const overrideKeys = [
    ...draft.city_overrides.map(key),
    ...live.city_overrides.map(key).filter((k) => !draftOverrides.has(k)),
  ];
  for (const k of overrideKeys) {
    const before = liveOverrides.get(k);
    const after = draftOverrides.get(k);
    const any = (after ?? before)!;
    rows.push([
      `${labelOf(any.item_type)} · ${cityName(any.city_id)}`,
      before ? formatInr(before.amount_paise, PAISE) : 'version amount',
      after ? formatInr(after.amount_paise, PAISE) : 'version amount',
    ]);
  }

  // Language and sampradaya amounts, the same way: a scope dropped by the draft is a change too.
  const scoped = (o: { ritual_language_id?: number | null; sampradaya_id?: number | null; item_type: string }) =>
    `${o.ritual_language_id ?? 0}:${o.sampradaya_id ?? 0}:${o.item_type}`;
  const liveScopes = new Map((live.scope_overrides ?? []).map((o) => [scoped(o), o]));
  const draftScopes = new Map((draft.scope_overrides ?? []).map((o) => [scoped(o), o]));
  const scopeKeys = [...draftScopes.keys(), ...[...liveScopes.keys()].filter((k) => !draftScopes.has(k))];
  for (const k of scopeKeys) {
    const before = liveScopes.get(k);
    const after = draftScopes.get(k);
    const any = (after ?? before)!;
    rows.push([
      `${labelOf(any.item_type)} · ${scopeName(any)}`,
      before ? formatInr(before.amount_paise, PAISE) : 'version amount',
      after ? formatInr(after.amount_paise, PAISE) : 'version amount',
    ]);
  }

  rows.push(['Base total', formatInr(live.base_total_paise, PAISE), formatInr(draft.base_total_paise, PAISE)]);
  return rows;
}

export function changedOnly(rows: readonly PriceDiffRow[]): PriceDiffRow[] {
  return rows.filter(([, before, after]) => before !== after);
}
